import { use, useEffect, useRef, useState } from "react";
import { useScript } from "../hooks/useScript";

interface TcPlayerProps {
  videoUrl?: string;
  advertiseVideoUrl?: string;
  fallbackUrl?: string;
  language?: string;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  setStartTime: React.Dispatch<React.SetStateAction<string>>;
  setEndTime: React.Dispatch<React.SetStateAction<string>>;
}

declare global {
  interface Window {
    TCPlayer: any;
  }
}

const TcPlayer: React.FC<TcPlayerProps> = ({
  videoUrl,
  advertiseVideoUrl,
  fallbackUrl,
  language = "en",
  autoplay = true,
  muted = true,
  loop = true,
  setStartTime,
  setEndTime,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const playerIdRef = useRef<string>(
    `tc-player-${Math.random().toString(36).substring(2, 9)}`
  );
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [showAdvertisement, setShowAdvertisement] = useState(!videoUrl);
  const [seconds, setSeconds] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const configRes = await fetch(`${process.env.PUBLIC_URL}/config.json`);
        const config = await configRes.json();
        setSeconds(config.checkBySeconds);
        setMinutes(config.checkByMinutes);
        setConfigLoaded(true);
      } catch (error) {
        console.error("Failed to load config.json", error);
      }
    };

    fetchConfig();
  }, []);

  useEffect(() => {
    if (!videoUrl && !advertiseVideoUrl) {
      setShowAdvertisement(true);
    }
  }, [videoUrl, advertiseVideoUrl]);

  const hlsStatus = useScript(
    "https://web.sdk.qcloud.com/player/tcplayer/release/v4.5.4/libs/hls.min.0.13.2m.js"
  );
  const tcplayerStatus = useScript(
    "https://web.sdk.qcloud.com/player/tcplayer/release/v4.9.1/tcplayer.v4.9.1.min.js"
  );
  const txplayerStatus = useScript(
    "https://web.sdk.qcloud.com/player/tcplayer/release/v4.9.1/libs/TXLivePlayer-1.3.4.min.js"
  );

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const startPolling = () => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(() => {
      fetchLiveStreamInfo();
    }, seconds);
  };

  const fetchLiveStreamInfo = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/LiveStream/GetLivePlan?apikey=${process.env.REACT_APP_API_KEY}`
      );
      const liveStreamInfo = await res.json();

      if (liveStreamInfo?.data?.playAddress) {
        const newUrl = liveStreamInfo.data.playAddress;

        if (newUrl !== videoUrl) {
          playerRef.current?.src([{ src: newUrl }]);

          const playResult = playerRef.current?.play?.();
          if (playResult && typeof playResult.catch === "function") {
            playResult.catch((err: any) =>
              console.error("Autoplay error after fetch:", err)
            );
          }
        } else {
          console.log("⏩ Skipped update, URL unchanged:", newUrl);
        }

        setStartTime(liveStreamInfo.data.startTime);
        setEndTime(liveStreamInfo.data.endTime);
      } else {
        setStartTime("");
        setEndTime("");
      }
    } catch (err) {
      console.error("Failed to fetch live stream info:", err);
    }
  };

  useEffect(() => {
    const allScriptsLoaded =
      tcplayerStatus === "ready" &&
      txplayerStatus === "ready" &&
      hlsStatus === "ready" &&
      typeof window.TCPlayer !== "undefined";

    setIsPlayerReady(allScriptsLoaded);
  }, [tcplayerStatus, txplayerStatus, hlsStatus]);

  useEffect(() => {
    if (!isPlayerReady || !videoUrl || !configLoaded) return;

    const minutePolling = setInterval(() => {
      fetchLiveStreamInfo();
    }, minutes);

    return () => {
      clearInterval(minutePolling);
    };
  }, [isPlayerReady, videoUrl, minutes, configLoaded]);

  useEffect(() => {
    if (!isPlayerReady || !videoUrl || !containerRef.current) return;

    if (playerRef.current) {
      try {
        playerRef.current.dispose();
      } catch (e) {
        console.warn("Error disposing previous player:", e);
      }
      playerRef.current = null;
    }

    const videoElement = document.createElement("video");
    videoElement.id = playerIdRef.current;
    videoElement.className = `tcplayer w-full h-full min-h-[200px] sm:min-h-[300px] md:min-h-[490px] lg:min-h-[480px]`;
    videoElement.muted = muted;
    videoElement.setAttribute("playsInline", "true");
    videoElement.setAttribute("webkit-playsinline", "true");
    videoElement.setAttribute("preload", "auto");

    if (containerRef.current.firstChild) {
      containerRef.current.removeChild(containerRef.current.firstChild);
    }
    containerRef.current.appendChild(videoElement);

    const initializePlayer = () => {
      try {
        const player = new window.TCPlayer(playerIdRef.current, {
          autoplay,
          loop,
          muted,
          sources: [{ src: videoUrl }],
          language,
          licenseUrl:
            "https://license.vod-pro.com/license/v2/1324854731_1/v_cube.license",
          controls: true,
          poster: "",
        });

        playerRef.current = player;

        player.on("error", (error: any) => {
          // console.error("TCPlayer error:", error);
          setShowAdvertisement(true);
          if (configLoaded) startPolling();
        });

        player.on("playing", () => {
          // console.log("Video is playing normally again");
          setShowAdvertisement(false);
          stopPolling();
        });

        player.on("loadedmetadata", () => {
          // console.log("Video metadata loaded");
        });

        player.on("waiting", () => {
          // console.log("Video is buffering");
        });

        const originalPlay = player.play;
        player.play = function () {
          try {
            const result = originalPlay.apply(this, arguments);
            if (result && typeof result.catch === "function") {
              result.catch((error: any) => {
                // console.error("Play promise error:", error);
              });
            }
            return result;
          } catch (error) {
            // console.error("Error during play():", error);
            return Promise.reject(error);
          }
        };
      } catch (error) {
        // console.error("Error initializing TCPlayer:", error);
      }
    };

    setTimeout(initializePlayer, 300);

    return () => {
      stopPolling();
      if (playerRef.current) {
        try {
          playerRef.current.dispose();
        } catch (e) {
          console.warn("Error during cleanup:", e);
        }
        playerRef.current = null;
      }
    };
  }, [isPlayerReady, videoUrl, language, autoplay, loop, muted]);

  // 👇 Switch from ad to livestream when videoUrl becomes valid
  useEffect(() => {
    if (!isPlayerReady || !videoUrl) return;

    if (showAdvertisement && playerRef.current) {
      console.log("Switching from ad to live stream...");

      setShowAdvertisement(false);

      playerRef.current.src([{ src: videoUrl }]);
      playerRef.current
        .play?.()
        .catch((err: any) =>
          console.error("Autoplay error after switching source:", err)
        );

      stopPolling();
    }
  }, [videoUrl, isPlayerReady]);

  return (
    <div className="relative">
      <link
        rel="stylesheet"
        href="https://web.sdk.qcloud.com/player/tcplayer/release/v4.5.4/tcplayer.min.css"
      />

      <div
        ref={containerRef}
        className={`${showAdvertisement ? "hidden" : ""} w-full h-full`}
      />

      <div className={`${!showAdvertisement ? "hidden" : ""} w-full h-full`}>
        <video
          id="advertisement-player"
          src={advertiseVideoUrl || fallbackUrl}
          autoPlay
          controls
          muted
          loop
          playsInline
          webkit-playsinline="true"
          preload="auto"
          className={`tcplayer w-full h-full min-h-[200px] sm:min-h-[300px] md:min-h-[490px] lg:min-h-[480px] bg-black`}
        />
      </div>

      {!isPlayerReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="text-gray-500">Loading player...</div>
        </div>
      )}
    </div>
  );
};

export default TcPlayer;
